import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInstructors, createInstructor, toggleInstructorStatus } from '../../../api/adminApi';
import Toast from '../../../components/admin/Toast';
import { toTitleCase } from '../../../utils/format';
import { sanitizeFields } from '../../../utils/sanitize';

const AV_COLORS = ['#5B6ABF','#1A7048','#E8440A','#D4882A','#8A6FB0','#2E8B8B','#C74D3A','#F7C91B'];
function avColor(id) {
  let h = 0;
  for (let i = 0; i < String(id).length; i++) h = String(id).charCodeAt(i) + ((h << 5) - h);
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
}
function initials(name = '') { return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(); }
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { day:'numeric', month: 'short', year: 'numeric' });
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function InstructorList() {
  const [instructors, setInstructors] = useState([]);
  const [modal,   setModal]   = useState(null);
  const [toast,   setToast]   = useState(null);
  const [form,    setForm]    = useState({ full_name: '', email: '', password: '', department: '' });
  const [saving,   setSaving]   = useState(false);
  const [toggling, setToggling] = useState(null); // instructor id being toggled
  const [deactivateTarget, setDeactivateTarget] = useState(null); // { id, full_name }
  const [deactivateReason, setDeactivateReason] = useState('');
  const [search,           setSearch]           = useState('');
  const navigate = useNavigate();

  const load = () => getInstructors().then(d => setInstructors(d.instructors)).catch(console.error);

  async function handleToggleStatus(e, inst) {
    e.stopPropagation();
    if (inst.status === 'Active') {
      // Going inactive — show reason modal first
      setDeactivateReason('');
      setDeactivateTarget({ id: inst.id, full_name: inst.full_name });
      return;
    }
    // Going active — no reason needed
    await applyStatusChange(inst.id, inst.full_name, 'Active', '');
  }

  async function applyStatusChange(id, name, newStatus, reason) {
    setToggling(id);
    try {
      const res = await toggleInstructorStatus(id, { new_status: newStatus, reason });
      setInstructors(prev => prev.map(i => i.id === id ? { ...i, status: res.status } : i));
      setToast({ msg: `${toTitleCase(name)} set to ${res.status}`, type: res.status === 'Active' ? 'success' : 'error' });
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally {
      setToggling(null);
    }
  }

  async function handleDeactivateConfirm() {
    const { id, full_name } = deactivateTarget;
    setDeactivateTarget(null);
    await applyStatusChange(id, full_name, 'Inactive', deactivateReason);
  }
  useEffect(() => { load(); }, []);

  function openAdd() {
    setForm({ full_name: '', email: '', password: '', department: '' });
    setModal('add');
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = sanitizeFields({ ...form }, ['full_name', 'department']);
      const res = await createInstructor(payload);
      load();
      setModal(null);
      setToast({ msg: `${form.full_name} added successfully`, type: 'success' });
      if (res.email_sent) {
        setTimeout(() => setToast({ msg: `Welcome email sent to ${form.email}`, type: 'success' }), 2500);
      }
    } catch (err) {
      const m = err.message?.toLowerCase() || '';
      const msg = m.includes('already in use') || m.includes('already exist') || m.includes('409')
        ? `This email is already registered. Please use a different email address.`
        : err.message;
      setToast({ msg, type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  const q        = search.toLowerCase();
  const filtered = instructors.filter(i =>
    i.full_name?.toLowerCase().includes(q) || i.email?.toLowerCase().includes(q)
  );

  return (
    <>
      {/* ── Header ── */}
      <div className="ph">
        <div>
          <div className="bc">Admin / Instructors</div>
          <div className="pt">Instructor Management</div>
          <div className="ps">{instructors.length} instructor{instructors.length !== 1 ? 's' : ''} on the platform</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="search-box">
            <input
              placeholder="Search by name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: 220 }}
            />
          </div>
          <button className="btn btn-dark btn-sm" onClick={openAdd}>+ New Instructor</button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="body">
        <div className="card" style={{ padding: 0 }}>
          <div className="ct" style={{ padding: '16px 20px' }}>All Instructors</div>
          {instructors.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">👤</div>
              <div>No instructors yet</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Instructor</th>
                    <th>Department</th>
                    <th>Courses</th>
                    <th>Joining Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(inst => {
                    const courseLabel = inst.courses?.length
                      ? inst.courses.map(c => toTitleCase(c.title)).join(', ')
                      : 'None';
                    return (
                      <tr
                        key={inst.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/admin/instructors/${inst.id}`)}
                      >
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div
                              className="av"
                              style={{ background: avColor(inst.id), color: 'white', fontSize: 11, flexShrink: 0 }}
                            >
                              {initials(inst.full_name)}
                            </div>
                            <div>
                              <div style={{ fontWeight: 500 }}>{toTitleCase(inst.full_name)}</div>
                              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{inst.email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--ink)' }}>
                          {inst.department ? toTitleCase(inst.department) : <span style={{ color: 'var(--subtle)' }}>—</span>}
                        </td>
                        <td style={{ fontSize: 12.5, color: courseLabel === 'None' ? 'var(--subtle)' : 'var(--ink)' }}>
                          {courseLabel}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                          {fmtDate(inst.created_at)}
                        </td>
                        <td onClick={e => handleToggleStatus(e, inst)}>
                          <span
                            className={`b ${inst.status === 'Active' ? 'b-g' : 'b-r'}`}
                            style={{ cursor: toggling === inst.id ? 'wait' : 'pointer', userSelect: 'none' }}
                            title={inst.status === 'Active' ? 'Click to deactivate' : 'Click to activate'}
                          >
                            {toggling === inst.id ? '…' : inst.status === 'Active' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Add Instructor Modal ── */}
      {modal === 'add' && (
        <div className="modal-overlay">
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              Add Instructor
              <button className="btn btn-o btn-sm" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="fg">
                <label className="fl">Full Name</label>
                <input className="li" placeholder="Dr. Jane Smith"
                  value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} required />
              </div>
              <div className="fg">
                <label className="fl">Email</label>
                <input className="li" type="email" placeholder="j.smith@edu"
                  value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
              </div>
              <div className="fg">
                <label className="fl">Password</label>
                <input className="li" type="password" value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Leave blank to auto-generate password" />
              </div>
              <div className="fg">
                <label className="fl">Department</label>
                <input className="li" placeholder="e.g. AI & Marketing"
                  value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <button type="button" className="btn btn-o" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-p" disabled={saving}>
                  {saving ? 'Creating…' : 'Add Instructor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Deactivate Reason Modal ── */}
      {deactivateTarget && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              Deactivate Instructor
              <button className="btn btn-o btn-sm" onClick={() => setDeactivateTarget(null)}>✕</button>
            </div>
            <p style={{ fontSize: 13.5, color: 'var(--ink)', marginBottom: 16, lineHeight: 1.6 }}>
              You are about to deactivate <strong>{toTitleCase(deactivateTarget.full_name)}</strong>.
              An email will be sent notifying them. You can include a reason below.
            </p>
            <div className="fg">
              <label className="fl">Reason <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional)</span></label>
              <textarea
                className="li"
                rows={3}
                placeholder="e.g. Contract ended, account under review…"
                value={deactivateReason}
                onChange={e => setDeactivateReason(e.target.value)}
                style={{ resize: 'vertical', minHeight: 80 }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button className="btn btn-o" onClick={() => setDeactivateTarget(null)}>Cancel</button>
              <button
                className="btn"
                style={{ background: '#C74D3A', color: 'white' }}
                onClick={handleDeactivateConfirm}
              >
                Deactivate &amp; Notify
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={typeof toast === 'string' ? toast : toast.msg}
          type={typeof toast === 'string' ? 'success' : toast.type}
          onDone={() => setToast(null)}
        />
      )}
    </>
  );
}
